import { useState, type FC } from 'react';
import { CloseIcon, SendIcon, SparklesAIIcon } from './ProjectMemberIcons';

interface ProjectMemberAIModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAskAI: (prompt: string) => Promise<{ reply: string; suggested_actions: string[] }>;
}

export const ProjectMemberAIModal: FC<ProjectMemberAIModalProps> = ({
  isOpen,
  onClose,
  onAskAI,
}) => {
  const [prompt, setPrompt] = useState('');
  const [reply, setReply] = useState<string | null>(null);
  const [suggestedActions, setSuggestedActions] = useState<string[]>([]);
  const [isAsking, setIsAsking] = useState(false);

  if (!isOpen) return null;

  const handleAsk = async (textToAsk?: string) => {
    const query = textToAsk || prompt;
    if (!query.trim()) return;
    setIsAsking(true);
    try {
      const res = await onAskAI(query.trim());
      setReply(res.reply);
      setSuggestedActions(res.suggested_actions || []);
    } finally {
      setIsAsking(false);
    }
  };

  const sampleQuestions = [
    'Какие дедлайны по моим заявкам на этой неделе?',
    'Подобрать журналы Scopus для статьи по компьютерной лингвистике',
    'Как создать опрос и сгенерировать публичную ссылку?',
  ];

  return (
    <div className="cab-overlay">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3.5">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-400">
              <SparklesAIIcon className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">Super AI · Участник проекта</h3>
              <p className="text-[11px] text-slate-400">Role-scoped intelligence по проектам, дедлайнам и инструментам</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1 rounded-lg"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 space-y-3.5 text-xs">
          <div className="flex flex-wrap gap-1.5">
            {sampleQuestions.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => {
                  setPrompt(q);
                  handleAsk(q);
                }}
                className="rounded-lg bg-slate-800/80 hover:bg-slate-700 border border-slate-700 px-2.5 py-1 text-[11px] text-slate-300 text-left transition-colors"
              >
                {q}
              </button>
            ))}
          </div>

          <div className="relative">
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Спросите AI-ассистента о статусе заявок, журналах или форматировании..."
              rows={3}
              className="w-full rounded-xl border border-slate-700 bg-slate-950 p-3 pr-10 text-xs text-white placeholder-slate-500 focus:border-amber-500 focus:outline-none"
            />
            <button
              aria-label="Отправить запрос"
              disabled={isAsking || !prompt.trim()}
              onClick={() => handleAsk()}
              className="absolute right-2.5 bottom-3.5 flex h-7 w-7 items-center justify-center rounded-lg bg-amber-500 text-slate-950 hover:bg-amber-400 disabled:opacity-40"
            >
              <SendIcon className="h-3.5 w-3.5" />
            </button>
          </div>

          {reply && (
            <div className="rounded-xl border border-slate-800 bg-slate-950/70 p-4 space-y-2.5">
              <div className="flex items-center gap-1.5 text-amber-400 font-bold text-[11px]">
                <SparklesAIIcon className="h-3.5 w-3.5" />
                <span>Ответ ролевого ассистента</span>
              </div>
              <p className="text-slate-200 leading-relaxed">{reply}</p>

              {suggestedActions.length > 0 && (
                <div className="pt-2 border-t border-slate-800/80">
                  <span className="text-[10px] uppercase font-bold text-slate-500 block mb-1.5">
                    Рекомендуемые действия:
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {suggestedActions.map((act) => (
                      <span
                        key={act}
                        className="rounded bg-slate-800 px-2 py-0.5 text-[11px] text-emerald-400 font-medium border border-slate-700"
                      >
                        ✓ {act}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
